import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Info } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  // Check if user came from subscription flow
  const fromSubscription = searchParams.get('subscription') === 'pending';
  const planName = searchParams.get('plan');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        phone: formData.phone || null
      });
      
      toast.success(`Welcome to CareKids WI, ${user.name}!`);
      
      // Redirect to subscription page (they need to subscribe first)
      navigate('/subscribe');
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different error types
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response) {
        // Server responded with an error
        errorMessage = error.response.data?.detail || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received (network issue)
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else {
        // Error in setting up the request
        errorMessage = error.message || 'An unexpected error occurred.';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/icons/icon-192x192.png?v=4"
              alt="CareKids WI"
              className="w-12 h-12 object-contain"
              data-testid="register-logo"
            />
            <span className="font-heading font-bold text-2xl">CareKids WI</span>
          </Link>
        </div>

        <Card className="card-clay">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="font-heading text-2xl font-bold">Register Your Center</CardTitle>
            <CardDescription>
              Create your child care center administrator account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Center Name / Your Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Sunshine Child Care Center"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="input-clay"
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Business Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@yourchildcare.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="input-clay"
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Business Phone (Optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="input-clay"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Account Type</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => handleChange('role', value)}
                  disabled={fromSubscription}
                >
                  <SelectTrigger className="input-clay" data-testid="register-role-select">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Child Care Center Administrator</SelectItem>
                    <SelectItem value="provider">Child Care Provider / Owner</SelectItem>
                  </SelectContent>
                </Select>
                {!fromSubscription && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-2">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      A subscription is required to access the platform.{' '}
                      <Link to="/pricing" className="font-semibold underline">
                        View pricing plans
                      </Link>
                    </p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Are you a parent? Parents are invited by their child care provider.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="input-clay pr-10"
                    required
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className="input-clay"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Checkbox 
                  id="terms" 
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5"
                  data-testid="terms-checkbox"
                />
                <Label 
                  htmlFor="terms" 
                  className="text-sm cursor-pointer leading-relaxed"
                >
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-primary hover:underline font-semibold">
                    Terms and Conditions
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" className="text-primary hover:underline font-semibold">
                    Privacy Policy
                  </Link>
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full btn-primary" 
                disabled={loading || !termsAccepted}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
